<!--
  工程编辑器主视图（布局壳）

  职责：
  1. 组合各子组件（TopBar / SceneTree / Preview / Resource / EventPalette / EventList / EventProps）
  2. 管理三栏分割拖拽（左侧场景树宽度、右侧面板宽度、中间预览/资源比例、右侧事件列表/属性比例）
  3. Ctrl+S / 顶栏保存按钮统一保存工程

  设计原则：
  - 工程数据仅保存一份（workspace store），子组件通过 store 引用获取
  - 本组件不持有业务逻辑，仅负责布局编排
-->
<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRouter } from 'vue-router'
import {
  NDropdown,
  NText,
  useMessage,
} from 'naive-ui'
import SceneEventList from '@/events/list/SceneEventList.vue'
import EventPropertyRouter from '@/events/editor/EventPropertyRouter.vue'
import TopBar from '@/components/workspace/TopBar.vue'
import SceneTreePanel from '@/components/workspace/SceneTreePanel.vue'
import CustomUIPropertyRouter from '@/components/workspace/CustomUIPropertyRouter.vue'
import PreviewCanvas from '@/components/workspace/PreviewCanvas.vue'
import ResourceBrowser from '@/components/workspace/ResourceBrowser.vue'
import EventPalette from '@/components/workspace/EventPalette.vue'
import SettingsModal from '@/components/workspace/SettingsModal.vue'
import MaterialLibraryModal from '@/material/web/MaterialLibraryModal.vue'
import PublishPanel from '@/publish/components/PublishPanel.vue'
import TextImportModal from '@/features/text-import/TextImportModal.vue'
import type MainProject from '@/model/MainProject'
import { buildPreviewTestSession, type PreviewTestSession } from '@/features/runtime-test/buildPreviewTestSession'
import { useProjectViewerEventActions } from '@/features/workspace/composables/useProjectViewerEventActions'
import { useProjectViewerLayout } from '@/features/workspace/composables/useProjectViewerLayout'
import { useWorkspaceStore } from '@/store/workspace'
import { useSceneEditorStore } from '@/store/sceneEditor'
import { useProjectLoadingStore } from '@/store/projectLoading'
import { useAuthStore } from '@/features/auth'
import { ENABLE_CUSTOM_UI_EDITOR } from '@/config/featureFlags'
import { buildProjectWordCountByMap, type ProjectWordCountByMap } from '@/features/project-stats/wordCount'

const router = useRouter()
const message = useMessage()
const workspace = useWorkspaceStore()
const editor = useSceneEditorStore()
const projectLoading = useProjectLoadingStore()
const auth = useAuthStore()

/* ——— UI 状态 ——— */

const settingsModalOpen = ref(false)
const materialLibraryOpen = ref(false)
const publishPanelOpen = ref(false)
const textImportOpen = ref(false)
const publishPanelRef = ref<{ requestOpen: () => Promise<void> } | null>(null)
const previewTestSession = ref<PreviewTestSession | null>(null)
const autoSaveTimer = ref<number | null>(null)
const savingProject = ref(false)
const totalWordCount = ref(0)
const wordCountByMapId = ref<ProjectWordCountByMap>(new Map())
const PORTRAIT_WORK_TABS = [
  { key: 'events', label: '剧情' },
  { key: 'resources', label: '资源' },
] as const
type PortraitWorkTabKey = (typeof PORTRAIT_WORK_TABS)[number]['key']
const AUTO_SAVE_INTERVAL_MS = 5 * 60 * 1000

/** 竖屏工作区顶栏：剧情 / 资源主视图是否选中。 */
function isPortraitWorkTabActive(key: PortraitWorkTabKey): boolean {
  return portraitMainView.value === key
}

/** 竖屏：点击剧情 / 资源主视图切换。 */
function onPortraitWorkTabClick(key: PortraitWorkTabKey) {
  setPortraitMainView(key)
}

/** 工程根对象快捷引用 */
const project = computed(() => workspace.project)
const previewAspectRatio = computed(() => {
  const width = Number(project.value?.screenHash?.width ?? 0)
  const height = Number(project.value?.screenHash?.height ?? 0)
  return width > 0 && height > 0 ? width / height : 16 / 9
})
const hasProjectSaveTarget = computed(() => Boolean(
  workspace.project
  && (
    workspace.rootDirectoryHandle
    || (auth.loggedIn && auth.uid && workspace.project.projectHeader.ID?.trim())
  ),
))
const hasBlockingModalOpen = computed(() =>
  settingsModalOpen.value
  || materialLibraryOpen.value
  || publishPanelOpen.value
  || textImportOpen.value,
)
const activeEventListTitle = computed(() => {
  const selection = editor.selectedCustomUi
  if (ENABLE_CUSTOM_UI_EDITOR && selection) {
    const uiName = editor.selectedCustomUiData?.Name?.trim() || 'UI界面'
    if (selection.eventSource === 'load') return `${uiName} / 初始化`
    if (selection.eventSource === 'after') return `${uiName} / 初始化完成`
    if (selection.eventSource === 'control') {
      const item = editor.selectedCustomUiItem
      const name = (item?.ImagePath2 || item?.Color || '').trim() || '按钮'
      return `${uiName} / ${name} / 点击事件`
    }
    return uiName
  }
  return editor.selectedMap ? editor.sceneTreeLabel(editor.selectedMap) : ''
})
const hasActiveEventList = computed(() => {
  return Boolean(
    editor.selectedMap
    || (ENABLE_CUSTOM_UI_EDITOR && editor.selectedCustomUi && editor.selectedCustomUi.eventSource !== 'none'),
  )
})
const landscapeEventPropsEmptyHint = computed(() =>
  editor.formatEventPropertyEmptyHint('在上方列表中选择一条事件'),
)
const portraitEventPropsEmptyHint = computed(() =>
  editor.formatEventPropertyEmptyHint('在事件列表中选择一条事件'),
)

const {
  centerPaneRef,
  cleanupDragging,
  dragEventHeight,
  dragPreviewHeight,
  dragRightWidth,
  dragSceneWidth,
  editorBodyRef,
  editorBodyVars,
  isPortraitLayout,
  isResourcePaneCollapsed,
  isScenePaneCollapsed,
  onPortraitHandlePointerDown,
  portraitEventsWorkspaceRef,
  portraitMainContentRef,
  portraitMainView,
  portraitPropsOpen,
  portraitStoryOpen,
  rightMainRef,
  setPortraitMainView,
  togglePortraitResourceView,
  togglePortraitStoryPane,
  toggleResourcePane,
  toggleScenePane,
} = useProjectViewerLayout({
  previewAspectRatio,
})
const scenePaneToggleCollapsed = computed(() =>
  isPortraitLayout.value ? !portraitStoryOpen.value : isScenePaneCollapsed.value,
)
const resourcePaneToggleCollapsed = computed(() =>
  isPortraitLayout.value ? portraitMainView.value !== 'resources' : isResourcePaneCollapsed.value,
)

function handleToggleScenePane() {
  if (isPortraitLayout.value) {
    togglePortraitStoryPane()
    return
  }
  toggleScenePane()
}

function handleToggleResourcePane() {
  if (isPortraitLayout.value) {
    togglePortraitResourceView()
    return
  }
  toggleResourcePane()
}

const {
  eventCtxOptions,
  eventCtxShow,
  eventCtxX,
  eventCtxY,
  closeEventContextMenu,
  executeEventAction,
  onEventCtxSelect,
  onEventMove,
  onSceneAddPlaceholderSelect,
  onSceneEventSelect,
  openEventContextMenu,
} = useProjectViewerEventActions(editor, message)

function canDropEventsAt(targetIndex: number, position: 'before' | 'after'): boolean {
  return editor.canMoveSelectedEventsTo(targetIndex, position)
}

/* ——— 快捷键：Ctrl+S 保存工程 ——— */

/**
 * 处理工程编辑器内的全局快捷键。
 *
 * @param e 键盘事件对象。
 * @returns 保存是否成功。
 */
function onProjectKeydown(e: KeyboardEvent) {
  if (!workspace.project) return
  if (
    publishPanelOpen.value
    || settingsModalOpen.value
    || materialLibraryOpen.value
  ) {
    return
  }
  const target = e.target
  if (
    target instanceof HTMLInputElement
    || target instanceof HTMLTextAreaElement
    || (target instanceof HTMLElement && target.isContentEditable)
  ) {
    return
  }
  if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
    e.preventDefault()
    void persistProject()
    return
  }
  if ((e.ctrlKey || e.metaKey) && !e.shiftKey && (e.key === 'z' || e.key === 'Z')) {
    if (editor.undoEditHistory()) {
      e.preventDefault()
    }
    return
  }
  if (
    (e.ctrlKey || e.metaKey)
    && ((e.key === 'y' || e.key === 'Y') || (e.shiftKey && (e.key === 'z' || e.key === 'Z')))
  ) {
    if (editor.redoEditHistory()) {
      e.preventDefault()
    }
    return
  }
  if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C')) {
    if (executeEventAction({ type: 'copy' })) {
      e.preventDefault()
    }
    return
  }
  if ((e.ctrlKey || e.metaKey) && (e.key === 'x' || e.key === 'X')) {
    if (executeEventAction({ type: 'cut' })) {
      e.preventDefault()
    }
    return
  }
  if ((e.ctrlKey || e.metaKey) && (e.key === 'v' || e.key === 'V')) {
    if (executeEventAction({ type: 'paste' })) {
      e.preventDefault()
    }
    return
  }
  if (e.key === 'Delete') {
    if (executeEventAction({ type: 'delete' })) {
      e.preventDefault()
    }
  }
}

/**
 * 刷新或关闭页面前提示未保存改动。
 *
 * 浏览器只允许显示原生离开确认，不能在 beforeunload 中打开自定义保存弹窗。
 */
function onProjectBeforeUnload(event: BeforeUnloadEvent): void {
  if (!workspace.project || !workspace.hasPendingChanges) return
  event.preventDefault()
  event.returnValue = ''
}

/** 按发布口径刷新工程总字数与各剧情字数表。 */
function refreshSavedWordCount(): void {
  const { byMapId, total } = buildProjectWordCountByMap(workspace.project as MainProject | null)
  wordCountByMapId.value = byMapId
  totalWordCount.value = total
}

/** 清理当前自动保存计时器。 */
function clearAutoSaveTimer(): void {
  if (autoSaveTimer.value === null) return
  window.clearTimeout(autoSaveTimer.value)
  autoSaveTimer.value = null
}

/** 从当前时刻重新开始计算下一次自动保存时间。 */
function scheduleAutoSave(): void {
  clearAutoSaveTimer()
  if (!workspace.project) return
  autoSaveTimer.value = window.setTimeout(() => {
    autoSaveTimer.value = null
    void persistProject({ auto: true })
  }, AUTO_SAVE_INTERVAL_MS)
}

/**
 * 保存当前工程，并将进度映射到统一加载面板。
 *
 * @param options 保存触发来源配置。
 * @returns 无返回值。
 */
async function persistProject(options: { auto?: boolean } = {}): Promise<boolean> {
  if (savingProject.value) {
    if (options.auto) scheduleAutoSave()
    return false
  }
  if (options.auto && !hasProjectSaveTarget.value) {
    scheduleAutoSave()
    return false
  }
  if (options.auto && projectLoading.active) {
    scheduleAutoSave()
    return false
  }
  if (options.auto && hasBlockingModalOpen.value) {
    scheduleAutoSave()
    return false
  }
  savingProject.value = true
  projectLoading.begin(options.auto ? '自动保存工程…' : '保存工程…', 'inline')
  try {
    projectLoading.set(workspace.rootDirectoryHandle ? 18 : 10, workspace.rootDirectoryHandle ? '写入本地工程…' : '准备上传工程…')
    await workspace.saveProject((progress) => {
      const mappedProgress =
        progress.stage === 'project-bin'
          ? 45 + Math.round(progress.percent * 0.18)
          : progress.stage === 'virtual-resources'
            ? 63 + Math.round(progress.percent * 0.22)
              : progress.stage === 'project-info'
                ? 93 + Math.round(progress.percent * 0.07)
                : Math.round(progress.percent * 0.4)
      projectLoading.set(mappedProgress, progress.detail || progress.fileName || '保存中…')
    })
    projectLoading.complete()
    refreshSavedWordCount()
    scheduleAutoSave()
    if (!options.auto) {
      message.success('保存成功')
    }
    return true
  } catch (e) {
    projectLoading.fail()
    scheduleAutoSave()
    if (!options.auto) {
      message.error(e instanceof Error ? e.message : '保存失败')
    }
    return false
  } finally {
    savingProject.value = false
  }
}

/* ——— 工程操作 ——— */

/**
 * 停止当前预览测试会话。
 *
 * @returns 无返回值。
 */
function stopPreviewTest() {
  previewTestSession.value = null
}

/** 横竖屏切换时若有测试在跑则停止，避免双 PreviewCanvas 重建导致引擎状态错乱。 */
watch(isPortraitLayout, () => {
  if (previewTestSession.value) stopPreviewTest()
})

/**
 * 在启动测试与停止测试之间切换。
 *
 * @returns 无返回值。
 */
function togglePreviewTest() {
  if (previewTestSession.value) {
    stopPreviewTest()
    return
  }
  void onRun()
}

/**
 * 返回启动页，并清理当前工程上下文。
 *
 * @returns 无返回值。
 */
async function goStartup() {
  stopPreviewTest()
  const saved = await persistProject()
  if (!saved) return
  workspace.clear()
  await router.push({ name: 'startup' })
}

/**
 * 打开发布面板；若实例尚未挂载则先显示容器。
 *
 * @returns 无返回值。
 */
async function onPublish() {
  if (!publishPanelRef.value) {
    publishPanelOpen.value = true
    return
  }
  await publishPanelRef.value.requestOpen()
}

/**
 * 构建并启动当前工程的预览测试环境。
 *
 * @returns 无返回值。
 */
async function onRun(mapId?: number) {
  if (!workspace.project) {
    message.warning('当前没有可测试的工程')
    return
  }
  const startMapId = Number(workspace.project.systemData?.totalSetting?.StartMapId ?? 0)
  const selectedMapId = Number.isFinite(mapId) ? Number(mapId) : startMapId

  projectLoading.begin('准备测试…', 'inline')
  try {
    editor.normalizeAllSceneEventStructures()
    projectLoading.set(18, '生成位图字体…')
    await workspace.generateBitmapFont()

    projectLoading.set(56, '构建测试运行时…')
    const session = await buildPreviewTestSession({
      project: workspace.project as MainProject,
      resourceRoots: workspace.virtualResourceRoots,
      selectedMapId,
      previewOverrides: workspace.resourcePreviewOverrides,
    })

    projectLoading.set(92, '启动画布测试…')
    previewTestSession.value = session
    projectLoading.complete()
  } catch (error) {
    projectLoading.fail()
    message.error(error instanceof Error ? error.message : '启动测试失败')
  }
}

/* ——— 生命周期 ——— */

onMounted(() => {
  if (!workspace.project && !workspace.openingProject) {
    router.replace({ name: 'startup' })
  }
  scheduleAutoSave()
  window.addEventListener('keydown', onProjectKeydown)
  window.addEventListener('beforeunload', onProjectBeforeUnload)
})

onBeforeUnmount(() => {
  cleanupDragging()
  clearAutoSaveTimer()
  window.removeEventListener('keydown', onProjectKeydown)
  window.removeEventListener('beforeunload', onProjectBeforeUnload)
})

watch(project, () => {
  refreshSavedWordCount()
  scheduleAutoSave()
}, { immediate: true })
</script>

<template>
  <div class="project-root">
    <p v-if="!project && workspace.openingProject" class="loading">正在载入工程数据…</p>
    <p v-else-if="!project" class="loading">正在返回启动页…</p>

    <div v-else class="editor-shell">
      <!-- 顶部工具栏 -->
      <TopBar
        :test-running="!!previewTestSession"
        :scene-pane-collapsed="scenePaneToggleCollapsed"
        :resource-pane-collapsed="resourcePaneToggleCollapsed"
        :portrait-layout="isPortraitLayout"
        :can-undo="editor.canUndoEditHistory"
        :can-redo="editor.canRedoEditHistory"
        @save-project="persistProject"
        @undo="editor.undoEditHistory()"
        @redo="editor.redoEditHistory()"
        @toggle-scene-pane="handleToggleScenePane"
        @toggle-resource-pane="handleToggleResourcePane"
        @material-library="materialLibraryOpen = true"
        @text-import="textImportOpen = true"
        @settings="settingsModalOpen = true"
        @publish="onPublish"
        @run="togglePreviewTest"
        @go-startup="goStartup"
      />

      <!-- 横屏 / PC：保留原三栏布局 -->
      <div
        v-if="!isPortraitLayout"
        ref="editorBodyRef"
        class="editor-body"
        :class="{
          'editor-body--scene-collapsed': isScenePaneCollapsed,
          'editor-body--resource-collapsed': isResourcePaneCollapsed,
        }"
        :style="editorBodyVars"
      >
        <!-- 左侧：场景树 -->
        <div class="scene-pane-host">
          <SceneTreePanel
            :total-word-count="totalWordCount"
            :word-count-by-map-id="wordCountByMapId"
            @test-scene="onRun"
          />
        </div>
        <div
          class="drag-splitter drag-splitter-vertical drag-splitter--scene"
          :class="{ 'drag-splitter--collapsed': isScenePaneCollapsed }"
          title="拖拽调整场景树宽度"
          @pointerdown="dragSceneWidth"
        />

        <!-- 中间：预览 + 资源 -->
        <section ref="centerPaneRef" class="col-center">
          <PreviewCanvas :test-session="previewTestSession" @stop-test="stopPreviewTest" />
          <div
            class="drag-splitter drag-splitter-horizontal drag-splitter--resource"
            :class="{ 'drag-splitter--collapsed': isResourcePaneCollapsed }"
            title="拖拽调整预览区高度"
            @pointerdown="dragPreviewHeight"
          />
          <ResourceBrowser />
        </section>
        <div
          class="drag-splitter drag-splitter-vertical drag-splitter--right"
          title="拖拽调整右侧面板宽度"
          @pointerdown="dragRightWidth"
        />

        <!-- 右侧：事件选项 + 事件列表 + 事件属性 -->
        <section class="col-right">
          <EventPalette />
          <div ref="rightMainRef" class="right-main">
            <!-- 事件列表 -->
            <div class="event-list-pane">
              <template v-if="hasActiveEventList">
                <div class="scene-title">
                  {{ activeEventListTitle }}
                </div>
                <SceneEventList
                  class="scene-event-list-host"
                  :events="editor.selectedEventList"
                  :loading="false"
                  :revision="editor.sceneStructureRevision"
                  :label-revision="editor.sceneEventLabelRevision"
                  :selected-index="editor.selectedEventIndex"
                  :selected-indices="editor.selectedEventIndices"
                  :selected-add-key="editor.selectedAddPlaceholder?.key ?? null"
                  :collapsed-indices="editor.collapsedFoldIndices"
                  :can-drop-at="canDropEventsAt"
                  @select="onSceneEventSelect"
                  @select-add="onSceneAddPlaceholderSelect"
                  @contextmenu="openEventContextMenu"
                  @move="onEventMove"
                  @toggle-collapse="editor.toggleEventFold"
                />
              </template>
              <div v-else class="empty-hint">
                <n-text depth="3">
                  {{ ENABLE_CUSTOM_UI_EDITOR ? '请选择左侧剧情，或高级 UI 的初始化/按钮事件' : '请选择左侧剧情' }}
                </n-text>
              </div>
            </div>
            <div
              class="drag-splitter drag-splitter-horizontal inner-splitter"
              title="拖拽调整事件属性高度"
              @pointerdown="dragEventHeight"
            />

            <!-- 事件属性编辑器 -->
            <div class="event-props-pane">
              <template v-if="ENABLE_CUSTOM_UI_EDITOR && editor.selectedCustomUi && (!editor.selectedEvent || editor.selectedEventIndex === null)">
                <CustomUIPropertyRouter />
              </template>
              <template v-else-if="editor.canShowSelectedEventProperty">
                <EventPropertyRouter
                  :key="editor.selectedEventPropertyKey"
                  :event="editor.selectedEvent!"
                />
              </template>
              <div v-else class="empty-hint">
                <n-text depth="3">{{ landscapeEventPropsEmptyHint }}</n-text>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- 竖屏：独立布局，不复用横屏三栏样式 -->
      <div
        v-else
        ref="editorBodyRef"
        class="editor-body-portrait"
        :style="editorBodyVars"
      >
        <section class="portrait-preview-pane">
          <PreviewCanvas :test-session="previewTestSession" @stop-test="stopPreviewTest" />
        </section>

        <section class="portrait-bottom-pane">
          <div class="portrait-work-tabs">
            <button
              v-for="tab in PORTRAIT_WORK_TABS"
              :key="tab.key"
              type="button"
              class="portrait-work-tab"
              :class="{ 'portrait-work-tab--active': isPortraitWorkTabActive(tab.key) }"
              @click="onPortraitWorkTabClick(tab.key)"
            >
              {{ tab.label }}
            </button>
          </div>

          <div class="portrait-main-pane">
            <template v-if="portraitMainView === 'events'">
              <div ref="portraitEventsWorkspaceRef" class="portrait-events-workspace">
                <EventPalette />
                <div v-show="portraitStoryOpen" class="portrait-float-panel portrait-float-panel--story">
                  <SceneTreePanel
            :total-word-count="totalWordCount"
            :word-count-by-map-id="wordCountByMapId"
            @test-scene="onRun"
          />
                </div>
                <div ref="portraitMainContentRef" class="portrait-main-content">
                  <div class="portrait-event-list-pane">
                    <template v-if="hasActiveEventList">
                      <div class="scene-title">
                        {{ activeEventListTitle }}
                      </div>
                      <SceneEventList
                        class="scene-event-list-host"
                        :events="editor.selectedEventList"
                        :loading="false"
                        :revision="editor.sceneStructureRevision"
                        :label-revision="editor.sceneEventLabelRevision"
                        :selected-index="editor.selectedEventIndex"
                        :selected-indices="editor.selectedEventIndices"
                        :selected-add-key="editor.selectedAddPlaceholder?.key ?? null"
                        :collapsed-indices="editor.collapsedFoldIndices"
                        :can-drop-at="canDropEventsAt"
                        @select="onSceneEventSelect"
                        @select-add="onSceneAddPlaceholderSelect"
                        @contextmenu="openEventContextMenu"
                        @move="onEventMove"
                        @toggle-collapse="editor.toggleEventFold"
                      />
                    </template>
                    <div v-else class="empty-hint">
                      <n-text depth="3">
                        {{ ENABLE_CUSTOM_UI_EDITOR ? '请选择剧情树中的剧情，或高级 UI 的初始化/按钮事件' : '请点左侧浮层把手展开剧情树' }}
                      </n-text>
                    </div>
                  </div>

                  <div v-show="portraitPropsOpen" class="portrait-float-panel portrait-float-panel--props">
                    <div class="portrait-event-props-pane">
                      <template v-if="ENABLE_CUSTOM_UI_EDITOR && editor.selectedCustomUi && (!editor.selectedEvent || editor.selectedEventIndex === null)">
                        <CustomUIPropertyRouter />
                      </template>
                      <template v-else-if="editor.canShowSelectedEventProperty">
                        <EventPropertyRouter
                          :key="editor.selectedEventPropertyKey"
                          :event="editor.selectedEvent!"
                        />
                      </template>
                      <div v-else class="empty-hint">
                        <n-text depth="3">{{ portraitEventPropsEmptyHint }}</n-text>
                      </div>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  class="portrait-edge-handle portrait-edge-handle--story"
                  :class="{ 'portrait-edge-handle--open': portraitStoryOpen }"
                  :title="portraitStoryOpen ? '收起剧情树（可左右拖拽调宽）' : '展开剧情树（可左右拖拽调宽）'"
                  :aria-expanded="portraitStoryOpen"
                  @pointerdown="onPortraitHandlePointerDown('story', $event)"
                >
                  <span
                    class="portrait-edge-handle__tri"
                    :class="portraitStoryOpen ? 'portrait-edge-handle__tri--left' : 'portrait-edge-handle__tri--right'"
                    aria-hidden="true"
                  />
                  <span class="portrait-edge-handle__label">剧情</span>
                </button>

                <button
                  type="button"
                  class="portrait-edge-handle portrait-edge-handle--props"
                  :class="{ 'portrait-edge-handle--open': portraitPropsOpen }"
                  :title="portraitPropsOpen ? '收起属性（可左右拖拽调宽）' : '展开属性（可左右拖拽调宽）'"
                  :aria-expanded="portraitPropsOpen"
                  @pointerdown="onPortraitHandlePointerDown('props', $event)"
                >
                  <span
                    class="portrait-edge-handle__tri"
                    :class="portraitPropsOpen ? 'portrait-edge-handle__tri--right' : 'portrait-edge-handle__tri--left'"
                    aria-hidden="true"
                  />
                  <span class="portrait-edge-handle__label">属性</span>
                </button>
              </div>
            </template>

            <div v-else class="portrait-resources-pane">
              <ResourceBrowser />
            </div>
          </div>
        </section>
      </div>
    </div>

    <!-- 弹窗 -->
    <SettingsModal v-model:show="settingsModalOpen" />
    <MaterialLibraryModal
      v-model:show="materialLibraryOpen"
    />
    <PublishPanel ref="publishPanelRef" v-model:show="publishPanelOpen" />
    <TextImportModal v-model:show="textImportOpen" :portrait-layout="isPortraitLayout" />
    <n-dropdown
      trigger="manual"
      placement="bottom-end"
      :show="eventCtxShow"
      :x="eventCtxX"
      :y="eventCtxY"
      :options="eventCtxOptions"
      @select="onEventCtxSelect"
      @clickoutside="closeEventContextMenu"
    />
  </div>
</template>

<style scoped>
.project-root {
  min-height: 100vh;
  background: var(--theme-bg-deep);
  color: var(--theme-text-primary);
  touch-action: manipulation;
}

.loading {
  padding: 24px;
  text-align: center;
  color: var(--theme-text-very-muted);
}

.editor-shell {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background: var(--theme-bg-surface);
  touch-action: manipulation;
}

.editor-body {
  --pv-scene-w: 196px;
  --pv-right-w: 320px;
  --pv-preview: 0.74;
  --pv-resource: 0.26;
  --pv-el: 0.58;
  --pv-ep: 0.42;
  position: relative;
  flex: 1;
  display: flex;
  gap: 0;
  min-height: 0;
  padding: 0;
  background: var(--theme-bg-surface);
}

.scene-pane-host {
  width: var(--pv-scene-w);
  flex-shrink: 0;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.col-center {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  background: var(--theme-bg-panel);
  border-top: 0;
  border-bottom: 0;
}

.col-right {
  width: var(--pv-right-w);
  min-width: var(--pv-right-min-w, 404px);
  flex-shrink: 0;
  display: flex;
  flex-direction: row;
  min-height: 0;
  overflow: hidden;
  background: var(--theme-bg-panel);
  border-top: 0;
  border-bottom: 0;
  border-right: 0;
  border-left: 0;
}

.right-main {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

/* ——— 拖拽分割条 ——— */
.drag-splitter {
  position: relative;
  flex-shrink: 0;
  user-select: none;
  touch-action: none;
  background: var(--theme-splitter-bg);
  border: 0;
}

.drag-splitter::before {
  content: '';
  position: absolute;
  inset: 50% auto auto 50%;
  transform: translate(-50%, -50%);
  background: var(--theme-bg-splitter-dot);
}

.drag-splitter::after {
  content: '';
  position: absolute;
  z-index: 1;
}

.drag-splitter-vertical {
  width: 5px;
  cursor: col-resize;
}

.drag-splitter-vertical::after {
  inset: 0 -6px;
}

.drag-splitter-vertical::before {
  width: 1px;
  height: calc(100% - 12px);
}

.drag-splitter-horizontal {
  height: 5px;
  cursor: row-resize;
}

.drag-splitter-horizontal::after {
  inset: -6px 0;
}

.drag-splitter-horizontal::before {
  width: calc(100% - 12px);
  height: 1px;
}

.drag-splitter--collapsed {
  cursor: pointer;
}

.editor-body--scene-collapsed .scene-pane-host {
  width: 0;
  min-width: 0;
  overflow: hidden;
  visibility: hidden;
  pointer-events: none;
}

.editor-body--resource-collapsed :deep(.resource-block) {
  flex: 0 0 0;
  min-height: 0;
  overflow: hidden;
  visibility: hidden;
  pointer-events: none;
}

.inner-splitter {
  background: var(--theme-splitter-bg);
}

/* ——— 事件列表区 / 事件属性区 ——— */
.event-list-pane,
.event-props-pane {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.event-list-pane {
  flex: var(--pv-el) 1 0;
  min-height: 0;
  border-bottom: 0;
}

.scene-title {
  flex-shrink: 0;
  padding: 7px 10px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: var(--theme-text-secondary);
  background: var(--theme-bg-raised);
  border-bottom: 1px solid var(--theme-border);
  text-transform: uppercase;
}

.event-list-pane .scene-event-list-host {
  flex: 1;
  min-height: 0;
  margin: 0;
}

.event-props-pane {
  flex: var(--pv-ep) 1 0;
  min-width: 0;
  min-height: 140px;
  overflow: hidden;
  padding: 0;
  box-sizing: border-box;
  background: var(--theme-bg-panel);
}

.event-props-pane > :not(.empty-hint) {
  flex: 1;
  min-height: 0;
  overflow: auto;
  transform: translateZ(0);
}

.empty-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  padding: 10px;
  text-align: center;
  color: var(--theme-text-very-muted);
  background: var(--theme-bg-panel);
}

:global(body.pane-resizing) {
  cursor: col-resize;
  user-select: none;
}

/* ——— 响应式断点 ——— */
@media (max-width: 1100px) {
  .editor-body {
    flex-direction: column;
  }

  .scene-pane-host,
  .col-right {
    width: 100% !important;
    min-width: 0;
  }

  .scene-pane-host {
    max-height: 240px;
  }

  .editor-body--scene-collapsed .scene-pane-host {
    width: 100% !important;
    max-height: 0;
  }

  .col-right {
    min-height: 520px;
  }

  .editor-body > .drag-splitter-vertical {
    display: block;
    height: 5px;
    width: 100%;
    cursor: row-resize;
  }

  .editor-body > .drag-splitter-vertical::before {
    width: calc(100% - 12px);
    height: 1px;
  }

}

@media (max-width: 900px) {
  .col-right {
    flex-direction: column;
  }

  .event-list-pane {
    width: 100%;
    border-right: 0;
  }
}

@media (max-width: 1180px) and (orientation: landscape) {
  .editor-body {
    flex-direction: row;
  }

  .scene-pane-host {
    width: min(var(--pv-scene-w), 164px) !important;
    max-height: none;
    flex-shrink: 0;
  }

  .editor-body--scene-collapsed .scene-pane-host {
    width: 0 !important;
    max-height: none;
  }

  .col-right {
    width: var(--pv-right-w) !important;
    min-width: var(--pv-right-min-w, 404px);
    min-height: 0;
    flex-direction: row;
  }

  .col-right :deep(.palette-pane) {
    flex: 0 0 110px;
    width: 110px !important;
    max-width: 132px;
    max-height: none;
    border-right: 1px solid var(--theme-border);
    border-bottom: 0;
  }

  .col-right :deep(.palette-scroll) {
    max-height: none;
  }

  .event-props-pane {
    min-width: 0;
  }

  .editor-body > .drag-splitter-vertical {
    display: block;
    width: 5px;
    height: auto;
    cursor: col-resize;
  }

  .editor-body > .drag-splitter-vertical::before {
    width: 1px;
    height: calc(100% - 12px);
  }

}

.editor-body-portrait {
  position: relative;
  flex: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, min(50%, calc(100vw / var(--pv-preview-aspect, 1.7777778)))) minmax(0, 1fr);
  min-height: 0;
  overflow: hidden;
  background: var(--theme-bg-surface);
}

.portrait-preview-pane {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: var(--theme-bg-panel);
}

.portrait-preview-pane :deep(.preview-block) {
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
}

.portrait-bottom-pane {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: 32px minmax(0, 1fr);
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: var(--theme-bg-panel);
}

.portrait-work-tabs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  min-width: 0;
  min-height: 0;
  background: var(--theme-bg-raised);
}

.portrait-work-tab {
  min-width: 0;
  padding: 0 8px;
  border: 0;
  border-right: 1px solid var(--theme-border);
  color: var(--theme-text-secondary);
  font-size: 12px;
  line-height: 32px;
  text-align: center;
  background: var(--theme-bg-raised);
}

.portrait-work-tab:last-child {
  border-right: 0;
}

.portrait-work-tab--active {
  color: var(--theme-accent);
  background: var(--theme-bg-panel);
  font-weight: 600;
}

.portrait-main-pane {
  position: relative;
  display: flex;
  flex-direction: row;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.portrait-main-content {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.portrait-resources-pane {
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.portrait-event-list-pane {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.portrait-main-pane :deep(.palette-pane) {
  position: relative;
  z-index: 1;
  flex: 0 0 110px;
  width: 110px !important;
  height: 100%;
  max-height: none;
  border-right: 1px solid var(--theme-border);
  border-bottom: 0;
}

.portrait-main-pane :deep(.palette-layout),
.portrait-main-pane :deep(.palette-scroll) {
  height: 100%;
  max-height: none;
}

.portrait-events-workspace {
  position: relative;
  flex: 1 1 0;
  display: flex;
  flex-direction: row;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.portrait-edge-handle {
  position: absolute;
  bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  z-index: 20;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 22px;
  min-height: 56px;
  padding: 8px 3px;
  box-sizing: border-box;
  border: 0;
  outline: none;
  box-shadow: none;
  -webkit-tap-highlight-color: transparent;
  appearance: none;
  color: var(--theme-text-secondary);
  background: color-mix(in srgb, var(--theme-bg-panel) 82%, var(--theme-bg-deep));
  font-size: 10px;
  line-height: 1.2;
  cursor: ew-resize;
  touch-action: none;
}

.portrait-edge-handle:focus,
.portrait-edge-handle:focus-visible,
.portrait-edge-handle:active {
  border: 0;
  outline: none;
  box-shadow: none;
}

.portrait-edge-handle--story {
  left: 0;
  border-radius: 0 8px 8px 0;
}

.portrait-edge-handle--story.portrait-edge-handle--open {
  left: var(--pv-portrait-story-w, 110px);
  transform: translateX(-100%);
}

.portrait-edge-handle--props {
  right: 0;
  left: auto;
  border-radius: 8px 0 0 8px;
}

.portrait-edge-handle--props.portrait-edge-handle--open {
  right: auto;
  left: calc(
    var(--pv-portrait-palette-w, 110px)
    + (100% - var(--pv-portrait-palette-w, 110px)) * (1 - var(--pv-portrait-props-ratio, 0.5))
  );
  transform: none;
}

.portrait-edge-handle--open {
  color: var(--theme-text-primary);
  background: color-mix(in srgb, var(--theme-bg-panel) 92%, var(--theme-bg-deep));
}

.portrait-edge-handle__tri {
  width: 0;
  height: 0;
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
}

.portrait-edge-handle__tri--left {
  border-right: 5px solid currentColor;
}

.portrait-edge-handle__tri--right {
  border-left: 5px solid currentColor;
}

.portrait-edge-handle__label {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  letter-spacing: 0.5px;
  font-size: 10px;
  font-weight: 600;
}

.portrait-float-panel {
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 4;
  display: flex;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: var(--theme-bg-panel);
}

.portrait-float-panel--story {
  left: 0;
  flex-direction: column;
  width: var(--pv-portrait-story-w, 110px);
  border-right: 1px solid var(--theme-border);
}

.portrait-float-panel--story :deep(.col-scene) {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
}

.portrait-float-panel--props {
  right: 0;
  flex-direction: row;
  width: calc(100% * var(--pv-portrait-props-ratio, 0.5));
  border-left: 1px solid var(--theme-border);
}

.portrait-event-list-pane .scene-event-list-host {
  flex: 1;
  width: 100%;
  min-height: 0;
  margin: 0;
}

.portrait-event-props-pane {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  box-sizing: border-box;
  background: var(--theme-bg-panel);
}

.portrait-event-props-pane > :not(.empty-hint) {
  flex: 1;
  min-height: 0;
  overflow: auto;
  transform: translateZ(0);
}
</style>
